import { useSyncExternalStore } from 'react'
import type { RouterOutputs } from '@/lib/api-types'

type Flow = RouterOutputs['progress']['list']['flows'][number]
export type AnnouncedTarget = Flow['target']
export type AnnouncedTitle = Flow['title']
type TitlePart = AnnouncedTitle[number]

/**
 * A flow a dialog announced the moment its button was pressed, before the
 * server answered (John, Nov 5 2026: "as soon as I click swap, withdraw or
 * whatever to confirm an action, the UI progress bar item needs to show up
 * right away"). The bar draws it among the server's flows until the server
 * lists the flow itself, which then takes its place.
 */
export type AnnouncedFlow = {
  id: number
  kind: Flow['kind']
  title: AnnouncedTitle
  symbols: string[]
  /** the server's record, once the dialog's request came back */
  target: AnnouncedTarget | null
  /** when the server answered: a read of the flows that answered after this and does not list it means it is over already, or never tracked */
  settledAt: number | null
  at: number
}

/** a request the server never answered goes by itself */
const STALE_MS = 60_000

let announced: AnnouncedFlow[] = []
let nextId = 1
const listeners = new Set<() => void>()
const emit = () => {
  for (const listener of listeners) listener()
}

export function announceFlow(input: { kind: Flow['kind']; title: AnnouncedTitle; symbols?: string[]; target?: AnnouncedTarget }): number {
  const id = nextId++
  const now = Date.now()
  announced = [...announced, { id, kind: input.kind, title: input.title, symbols: input.symbols ?? [], target: input.target ?? null, settledAt: input.target ? now : null, at: now }]
  emit()
  window.setTimeout(() => withdrawAnnounced(id), STALE_MS)
  return id
}

/** the server answered: the flow has its record, and the next read of the flows says whether it is listed */
export function settleAnnounced(id: number, target: AnnouncedTarget) {
  if (!announced.some((flow) => flow.id === id)) return
  announced = announced.map((flow) => (flow.id === id ? { ...flow, target, settledAt: Date.now() } : flow))
  emit()
}

export function withdrawAnnounced(id: number) {
  if (!announced.some((flow) => flow.id === id)) return
  announced = announced.filter((flow) => flow.id !== id)
  emit()
}

const subscribe = (listener: () => void) => {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}
const NONE: AnnouncedFlow[] = []
export const useAnnouncedFlows = () => useSyncExternalStore(subscribe, () => announced, () => NONE)

/** the words of a chip, in the server's shapes (src/api/progress.ts) */
export const text = (t: string): TitlePart => ({ type: 'text', text: t })
/** a token's logo alone, leading a trade's chip */
export const logoPart = (t: { symbol: string; address?: string | null; imageUrl?: string | null }): TitlePart => ({ type: 'logo', symbol: t.symbol, address: t.address ?? null, imageUrl: t.imageUrl ?? null })
/** an amount with its ticker: raw with its decimals, or in units */
export const amountPart = (symbol: string, a: { amountRaw?: string | null; decimals?: number | null; amountUi?: number | null }): TitlePart => ({ type: 'amount', symbol, amountRaw: a.amountRaw ?? null, decimals: a.decimals ?? null, amountUi: a.amountUi ?? null })
export const tokenPart = (t: { symbol: string; address?: string | null; imageUrl?: string | null; amountRaw?: string | null; decimals?: number | null; amountUi?: number | null }): TitlePart => ({
  type: 'token',
  symbol: t.symbol,
  address: t.address ?? null,
  imageUrl: t.imageUrl ?? null,
  amountRaw: t.amountRaw ?? null,
  decimals: t.decimals ?? null,
  amountUi: t.amountUi ?? null,
})
